import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import questions from './questions.json';
import './styles.css';
import Form from './Form';

import {
  convertType,
  addFormattedFields,
  getApiUrl,
  fetchSuggestion,
  getAverageRankStyles,
  renderFormattedItems,
  handleRefresh,
  handleClear,
  handleGenerate,
  simplifyQuestions,
} from './utilities';

/*
  QID (client-side) — UNIX TIMESTAMP STRATEGY (no PHP changes)

  Goal:
  - Keep your existing PHP endpoint untouched.
  - Send POST JSON { qid } to http://localhost:3666/test
  - QID must be a string that your PHP will accept (non-empty string).
  - You requested: "use timestamp unix timestamp".

  Canonical-ish shape we will emit:
    q_command_<unixSeconds>_<sequence>

  Where:
  - unixSeconds = Math.floor(Date.now()/1000)
  - sequence = Date.now() (ms) to reduce same-second collision probability
    (still purely timestamp derived, no randomness required)
*/

function getUnixTimestampSeconds() {
  return Math.floor(Date.now() / 1000);
}

function getUnixTimestampMillis() {
  return Date.now();
}

function hash4(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h).toString(36).slice(0, 4);
}

function generateQIDCommandFromTimestamp() {
  const unixSeconds = getUnixTimestampSeconds();
  const sequence = getUnixTimestampMillis(); // timestamp-derived integer, monotonic-ish
  return `q_command_${unixSeconds}_${sequence}`;
}

/*
  Heartbeat POST helper
  - Does not change PHP.
  - Sends { qid } only.
  - Returns parsed JSON if ok, else null.
*/
async function postHeartbeatTest(qid) {
  const url = new URL('http://localhost:3666/health');
  url.searchParams.set('qid', qid);

  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: { 'Accept': 'application/json' },
  });

  if (!res.ok) return null;
  return await res.json();
}

function formatItemsToLinkValue(items) {
  let stringified = JSON.stringify(items);
  let noWhitespace = stringified.replace(/\s+/g, '');
  let linkValue = encodeURIComponent(noWhitespace);

  if (linkValue.length < 10) {
    return 'error: ' + JSON.stringify({ linkValue, items });
  } else {
    return linkValue;
  }
}

/* ------------------------------------------------------------------ */
/* URL PREFILL: supports BOTH legacy + new starter formats             */
/*
  Your incoming URL format is:

    ?manifest=<json-object-map>
    #plan=<json-array-of-objects-with-value>

  Old format the app used to expect:
    ?manifest=[{id,a}, ...]
    #plan=[{id,a}, ...]

  New format we now support:
    ?manifest={"q-1":{value:"..."}, "q-2":{value:"..."}, ...}
    #plan=[{label:"...", type:"...", value:"..."}]

  Behavior:
  - If manifest/plan exists: apply answers into currentQuestions
  - Mark those question(s) status=1 (even if value is empty string)
  - Seed ranks=[8] so initial score starts high
  - Show main content immediately
  - Remove URL query/hash after applying (clean URL)
*/

function safeJsonParse(input) {
  if (!input) return null;
  try {
    return JSON.parse(input);
  } catch {
    return null;
  }
}

function decodeMaybe(str) {
  try {
    return decodeURIComponent(str);
  } catch {
    return str;
  }
}

function getFinishedFlagFromUrl() {
  if (typeof window === 'undefined') return false;
  try {
    const sp = new URLSearchParams(window.location.search || '');
    const v = sp.get('finished');
    return v === 'true';
  } catch {
    return false;
  }
}

function parseManifestFromQuery() {
  const sp = new URLSearchParams(window.location.search);
  const raw = sp.get('manifest');
  if (!raw) return null;

  // URLSearchParams already decodes %xx, but we support double-encoded inputs too
  const decoded = decodeMaybe(raw);
  return safeJsonParse(decoded) ?? safeJsonParse(raw);
}

function parsePlanFromHash() {
  const rawHash = window.location.hash || '';
  if (!rawHash || rawHash === '#') return null;

  const h = rawHash.startsWith('#') ? rawHash.slice(1) : rawHash;

  // supports #plan=<encoded-json>
  if (h.startsWith('plan=')) {
    const raw = h.slice('plan='.length);
    const decoded = decodeMaybe(raw);
    return safeJsonParse(decoded) ?? safeJsonParse(raw);
  }

  // supports #<encoded-json> (your example) or #<raw-json>
  const decoded = decodeMaybe(h);
  return safeJsonParse(decoded) ?? safeJsonParse(h);
}

function toAnswerString(v) {
  if (v === undefined || v === null) return null;
  if (typeof v === 'string') return v;
  try {
    return String(v);
  } catch {
    return null;
  }
}

// Extract an "answer" from multiple possible shapes.
// Supports:
// - { id: 'q-1', a: '...' }
// - { id: 'q-1', answer: '...' }
// - { id: 'q-1', value: '...' }
// - { label: '...', value: '...' }  (used by your #plan payload)
// - { value: '...' }
function extractAnswerFromUnknownShape(x) {
  if (x === undefined || x === null) return null;

  // primitive
  if (typeof x !== 'object') return toAnswerString(x);

  // object
  const v = x.a ?? x.answer ?? x.value ?? x.val;
  return toAnswerString(v);
}

function addPayloadToAnswerMap(payload, map) {
  if (!payload) return;

  // Array payload: [{id,a}] OR [{label,value}] etc
  if (Array.isArray(payload)) {
    for (const it of payload) {
      const id = it?.id;
      const ans = extractAnswerFromUnknownShape(it);
      if (!ans && ans !== '') {
        // null/undefined -> skip; ('' is allowed)
        continue;
      }

      if (id) {
        map[id] = ans;
      }
    }
    return;
  }

  // Object payload: { 'q-1': {value:'...'}, 'q-2': '...' }
  if (typeof payload === 'object') {
    for (const [id, it] of Object.entries(payload)) {
      const ans = extractAnswerFromUnknownShape(it);
      if (ans === null) continue;
      map[id] = ans;
    }
  }
}

function buildPrefillAnswerMap(manifestPayload, planPayload) {
  const map = {};

  // 1) query ?manifest=...
  addPayloadToAnswerMap(manifestPayload, map);

  // 2) hash #plan=...
  addPayloadToAnswerMap(planPayload, map);

  // Special case: your #plan payload often has NO ids (it’s usually an array of {label,type,value}).
  // In that case, treat the first non-null "value" as q-3.
  if (!Object.prototype.hasOwnProperty.call(map, 'q-3')) {
    let planText = null;
    if (typeof planPayload === 'string') {
      planText = planPayload;
    } else if (Array.isArray(planPayload)) {
      for (const it of planPayload) {
        const v = extractAnswerFromUnknownShape(it);
        if (v !== null) {
          planText = v;
          break;
        }
      }
    } else if (planPayload && typeof planPayload === 'object') {
      const v = extractAnswerFromUnknownShape(planPayload);
      if (v !== null) planText = v;
    }

    if (planText !== null) {
      map['q-3'] = planText;
    }
  }

  return map;
}

function applyPrefillToQuestions(questionList, answerMap) {
  if (!answerMap || !Object.keys(answerMap).length) return questionList;

  return (questionList || []).map(q => {
    const id = q?.id;
    if (!id) return q;

    // IMPORTANT: treat "present in manifest" as answered even if value is an empty string
    if (!Object.prototype.hasOwnProperty.call(answerMap, id)) return q;

    const newAnswer = answerMap[id];
    const answerStr = typeof newAnswer === 'string' ? newAnswer : String(newAnswer ?? '');

    return {
      ...q,
      answer: answerStr,
      status: 1,
    };
  });
}

/* ------------------------------------------------------------------ */
/* CONSOLE / EXPORT HELPERS                                            */
/*
  Goal:
  - The console must show ALL answers (not just the 4 "formatted" items).
  - Keep the "formattedItems" pipeline intact if you still want it,
    but add a "full answers" view + an export payload that includes q-3 plan.
*/

function normalizeAnswerForExport(v) {
  if (v === undefined || v === null) return '';
  if (typeof v === 'string') return v;
  try {
    return String(v);
  } catch {
    return '';
  }
}

function buildAllAnswersArray(questionList) {
  return (questionList || [])
    .filter(q => !q?.subq)
    .map(q => ({
      id: q?.id,
      a: normalizeAnswerForExport(q?.answer),
      status: q?.status ?? 0,
      rank: q?.rank ?? null,
      // keep the raw question text available for debugging in console view
      q: q?.q ?? '',
    }));
}

function buildAnsweredOnlyArray(questionList) {
  return (questionList || [])
    .filter(q => !q?.subq)
    .filter(q => {
      const a = normalizeAnswerForExport(q?.answer);
      return a.trim().length > 0;
    })
    .map(q => ({
      id: q?.id,
      a: normalizeAnswerForExport(q?.answer),
    }));
}

function App() {
  const textareaRef = useRef(null);
  const submitButtonRef = useRef(null);
  const didBootstrapRef = useRef(false);
  const didPrefillRef = useRef(false);
  const didFinishedReloadRef = useRef(false);

  const [currentQuestions, setCurrentQuestions] = useState(() => {
    const saved = localStorage.getItem('questions');
    return saved ? JSON.parse(saved) : questions;
  });

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answer, setAnswer] = useState('');
  const [modalMessage, setModalMessage] = useState(null);
  const [displayedText, setDisplayedText] = useState('');
  const [ranks, setRanks] = useState([]);
  const [averageRank, setAverageRank] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [hideForm, setHideForm] = useState(false);
  const [showConsole, setShowConsole] = useState(false);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [showMainContent, setShowMainContent] = useState(() =>
    getFinishedFlagFromUrl()
  );
  const [isFullsize, setIsFullsize] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState('default');
  const [isGenerating, setIsGenerating] = useState(() =>
    getFinishedFlagFromUrl()
  );
  const [progress, setProgress] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(600);
  const [connected, setConnected] = useState(null);
  const [testPassed, setTestPassed] = useState(null);
  const [prefillDone, setPrefillDone] = useState(false);

  /* -------------------------------------------------------------- */
  /* FINAL STEP HARD REFRESH (workaround for last-step UI glitch)    */
  /*
    At the final step it can "bug out" depending on extension timing/state.

    Fix:
    - When we enter the final Build step (isGenerating === true), do a *full* page
      refresh and append ?finished=true.
    - On load, ?finished=true skips the landing [Q] screen and shows the final step.
  */
  useEffect(() => {
    if (!isGenerating) return;

    // Already in finished mode -> don't loop.
    const sp = new URLSearchParams(window.location.search);
    const isFinished = sp.get('finished') === 'true';
    if (isFinished) return;

    // StrictMode guard (effects can run twice in dev)
    if (didFinishedReloadRef.current) return;
    didFinishedReloadRef.current = true;

    // Ensure the manifest prefill doesn't re-run on the refreshed URL.
    const url = new URL(window.location.href);
    url.searchParams.delete('manifest');
    url.searchParams.set('finished', 'true');

    // Ensure we skip the landing screen even if navigation is blocked.
    setShowMainContent(true);

    // Full reload (not SPA navigation)
    window.location.replace(url.toString());
  }, [isGenerating]);

  /* -------------------------------------------------------------- */
  /* URL PREFILL (run once, earliest)                                */
  useEffect(() => {
    // In React StrictMode (dev) effects can run twice — guard it.
    if (didPrefillRef.current) return;
    didPrefillRef.current = true;

    // Only run the starter auto-fill when a manifest param is present.
    // (This is the "starter link" flow from qinterest.)
    const sp = new URLSearchParams(window.location.search);
    const hasManifestParam = sp.has('manifest');

    const manifestPayload = parseManifestFromQuery();
    const planPayload = parsePlanFromHash();

    if (!hasManifestParam) {
      setPrefillDone(true);
      return;
    }

    // Build a single { [questionId]: answerString } map from whatever shape came in
    const answerMap = buildPrefillAnswerMap(manifestPayload, planPayload) || {};

    // Force defaults for q-5 and q-6 for starter manifests
    answerMap['q-5'] = 'monetization comes after launch';
    answerMap['q-6'] = 'data comes from Q';
    answerMap['q-7'] = 'marketing is done through AI generated videos posted to youtube this will be done manually';

    // Force completion of q-1 .. q-6 so the flow starts on q-7
    // (Even if some values are blank, treat them as complete for this starter flow.)
    const forceAnswered = ['q-1', 'q-2', 'q-3', 'q-4', 'q-5', 'q-6', 'q-7'];
    for (const id of forceAnswered) {
      if (!Object.prototype.hasOwnProperty.call(answerMap, id)) {
        answerMap[id] = '';
      }
    }

    // URL prefill should behave like a clean "starter session"
    try {
      localStorage.removeItem('questions');
      localStorage.removeItem('ranks');
    } catch {}

    const nextQuestions = applyPrefillToQuestions(questions, answerMap);

    try {
      localStorage.setItem('questions', JSON.stringify(nextQuestions));
    } catch {}

    // Seed score so everyone starts above the fail threshold
    const seededRanks = [8];
    try {
      localStorage.setItem('ranks', JSON.stringify(seededRanks));
    } catch {}

    setRanks(seededRanks);
    setCurrentQuestions(nextQuestions);

    // Open the main UI automatically when a manifest is present
    setShowMainContent(true);

    // Clean the URL so refresh doesn’t keep re-applying the starter manifest
    try {
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch {}

    setPrefillDone(true);
  }, []);
  /* -------------------------------------------------------------- */

  /*
    UPDATED: when connected, POST heartbeat to /test with auto qid
    - PHP expects POST + JSON body with { qid }
    - We keep your success logic the same: data exists => pass
  */
  useEffect(() => {
    let cancelled = false;

    if (connected) {
      const qid = generateQIDCommandFromTimestamp();

      postHeartbeatTest(qid)
        .then(data => {
          if (cancelled) return;

          if (data && Object.keys(data).length > 0) setTestPassed(true);
          else setTestPassed(false);
        })
        .catch(() => {
          if (cancelled) return;
          setTestPassed(false);
        });
    } else {
      setTestPassed(null);
    }

    return () => {
      cancelled = true;
    };
  }, [connected]);

  const [errorMessages] = useState([
    'your idea does not have high enough [Q] score to continue',
  ]);
  const [currentErrorMessageIndex, setCurrentErrorMessageIndex] = useState(0);

  const NEXUS_PROJECT_NAME =
    currentQuestions.find(q => q.id === 'q-1')?.answer || '';

  // BOOTSTRAP: run once, but ensure it sees prefilled questions
  useEffect(() => {
    if (!prefillDone) return;
    if (didBootstrapRef.current) return;
    didBootstrapRef.current = true;

    const classes = ['orange', 'purple', 'blue', 'green', 'pink'];
    document.body.classList.add(
      classes[Math.floor(Math.random() * classes.length)]
    );

    textareaRef.current?.focus();

    const nextIndex = currentQuestions.findIndex(q => q.status !== 1);
    if (nextIndex !== -1) {
      setCurrentQuestionIndex(nextIndex);
      fetchSuggestion(
        nextIndex,
        currentQuestions,
        setCurrentQuestions,
        setDisplayedText,
        setAnswer,
        setModalMessage,
        getPreviousQuestion(nextIndex, currentQuestions),
        setIsLoading,
        submitButtonRef
      );
    } else {
      // all answered -> keep index at -1
      setCurrentQuestionIndex(-1);
    }

    const savedRanks = JSON.parse(localStorage.getItem('ranks')) || [];
    setRanks(savedRanks);

    const handleKeyDown = e => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        submitButtonRef.current?.click();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [prefillDone, currentQuestions]);

  useEffect(() => {
    if (ranks.length > 0) {
      const avg = ranks.reduce((a, r) => a + r, 0) / ranks.length;
      setAverageRank(avg.toFixed(2));
    }

    const total = currentQuestions.filter(q => !q.subq).length;
    const completed = currentQuestions.filter(
      q => q.status === 1 && !q.subq
    ).length;
    const pct = (completed / total) * 100;
    setCompletionPercentage(pct.toFixed(2));
  }, [ranks, currentQuestions]);

  useEffect(() => {
    if (averageRank < 5) {
      const interval = setInterval(() => {
        const nextIdx = (currentErrorMessageIndex + 1) % errorMessages.length;
        setCurrentErrorMessageIndex(nextIdx);
        setModalMessage({ text: errorMessages[nextIdx], color: 'red' });
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [averageRank, errorMessages.length, currentErrorMessageIndex]);

  const handleInputChange = e => setAnswer(e.target.value);

  const handleSubmit = async () => {
    setHideForm(false);
    if (isLoading) return;

    setIsLoading(true);
    const currentQuestion = currentQuestions[currentQuestionIndex];
    const button = submitButtonRef.current;

    button?.classList.add('loading');

    const loaderDiv = document.createElement('div');
    loaderDiv.classList.add('loader');
    button?.parentElement?.appendChild(loaderDiv);

    try {
      const response = await fetch('/api/q', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: currentQuestion.q,
          answer,
          validator: currentQuestion.validator,

          // You can keep these if you want, but /api/q doesn't use them:
          // description: currentQuestion.description,
          // type: currentQuestion.type,
          // subq: currentQuestion.subq || [],
          // conversation: currentQuestions,
        }),
      });

      // Try to read server json even on non-200 so we can show real error
      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok) {
        const msg =
          data?.error ||
          data?.message ||
          `HTTP ${response.status} ${response.statusText}`;
        throw new Error(msg);
      }

      const rank = parseInt(data.rank, 10);
      if (!Number.isFinite(rank)) {
        throw new Error(`Invalid rank from server: ${String(data?.rank)}`);
      }

      const updatedRanks = [...ranks, rank];
      const updatedQuestions = currentQuestions.map((q, i) =>
        i === currentQuestionIndex
          ? {
              ...q,
              status: 1,
              rank,
              rankFeedback: data.rank_feedback,
              answer,
            }
          : q
      );

      setRanks(updatedRanks);
      localStorage.setItem('ranks', JSON.stringify(updatedRanks));

      const avg =
        updatedRanks.reduce((a, r) => a + r, 0) / updatedRanks.length;
      setAverageRank(avg.toFixed(2));

      if (avg < 5) {
        setModalMessage({
          text: errorMessages[currentErrorMessageIndex],
          color: 'red',
        });
        submitButtonRef.current.disabled = true;
        textareaRef.current.disabled = true;
        return;
      }

      setCurrentQuestions(updatedQuestions);
      localStorage.setItem('questions', JSON.stringify(updatedQuestions));

      const nextIndex = updatedQuestions.findIndex(q => q.status !== 1);
      if (nextIndex !== -1) {
        setCurrentQuestionIndex(nextIndex);
        fetchSuggestion(
          nextIndex,
          updatedQuestions,
          setCurrentQuestions,
          setDisplayedText,
          setAnswer,
          setModalMessage,
          getPreviousQuestion(nextIndex, updatedQuestions),
          setIsLoading,
          submitButtonRef
        );
      } else {
        setCurrentQuestionIndex(-1);
        // Keep the main UI visible so the final step (Generate/Build) can render.
        setShowMainContent(true);
        setHideForm(false);
      }

      setAnswer('');
    } catch (err) {
      setModalMessage({
        text: `API error: ${err?.message || 'unknown error'}`,
        color: 'yellow',
      });
      console.error('handleSubmit failed:', err);
    } finally {
      try {
        loaderDiv.remove();
      } catch {}
      button?.classList.remove('loading');
      setIsLoading(false);
    }
  };

  const getPreviousQuestion = (idx, list) =>
    list.slice(0, idx).reverse().find(q => q.status === 1);

  // Keep your existing formatted pipeline (used elsewhere)
  const formattedQuestions = addFormattedFields(currentQuestions);
  const formattedItems = renderFormattedItems(formattedQuestions);

  // NEW: full answers + answered-only export payload
  const allAnswersForConsole = buildAllAnswersArray(currentQuestions);
  const answeredOnlyForExport = buildAnsweredOnlyArray(currentQuestions);

  const encodedLink = formatItemsToLinkValue(answeredOnlyForExport);

  const chromeHasExtension =
    document.documentElement.classList.contains('nexus-platforms');
  const qid = `q_manifest_${hash4(encodedLink)}_01`;
  const linkTarget = chromeHasExtension
    ? `https://chatgpt.com/#Q_MANIFEST=${qid}|${encodedLink}`
    : `https://chatgpt.com/#Q_ERROR=chromeHasExtensionNOTFOUND`;

  const toggleConsole = () => setShowConsole(!showConsole);
  const closeConsole = () => setShowConsole(false);
  const toggleFullsize = () => setIsFullsize(!isFullsize);

  const handleThemeChange = e => {
    setSelectedTheme(e.target.value);
    document.body.className = e.target.value;
  };

  const handleClearContent = () => {
    console.log('CLEARING');
    setIsGenerating(false);
    handleClear(
      questions,
      setCurrentQuestions,
      setCurrentQuestionIndex,
      setDisplayedText,
      setAnswer,
      setRanks,
      setAverageRank,
      setModalMessage
    );
    setShowMainContent(false);
    window.location.href = '/';
  };

  const isLastQuestion = q => q.every(x => x.status === 1);

  console.log('isGenerating', isGenerating);

  useEffect(() => {
    const timer = setTimeout(() => {
      const found = document.querySelector('.nexus-platforms');
      setConnected(!!found);
    }, 3000); // wait 3 full seconds
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="page">
      <header className="header">
        <h1>nexus</h1>
      </header>

      <a href="https://runitbyq.com" className="top-logo">
        [Q]
      </a>

      <div className="q-extension-status-bar darkmode">
        <span className="status-label">[Q] Chrome Extension:</span>
        {connected === null ? (
          <span className="status-indicator checking">
            🟡 checking connection...
          </span>
        ) : connected ? (
          testPassed === null ? (
            <span className="status-indicator checking">
              🟡 checking [Q] service...
            </span>
          ) : testPassed ? (
            <span className="status-indicator connected">🟢 connected</span>
          ) : (
            <span className="status-indicator not-connected">
              🟡 Nexus Control Core not connected. Please install from <a href="https://nexus-platforms.com" target="_blank">https://nexus-platforms.com</a>.
            </span>
          )
        ) : (
          <span className="status-indicator not-connected">
            ❌ not connected —{' '}
            <a
              href="https://nexus-platforms.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              install extension
            </a>
          </span>
        )}
      </div>

      {modalMessage && (
        <div
          className="modal"
          style={{ backgroundColor: modalMessage.color, color: 'black' }}
        >
          <span className="close" onClick={() => setModalMessage(null)}>
            [×]
          </span>
          {modalMessage.text}
        </div>
      )}

      <img className="background" src="/nexus_console.png" alt="Nexus Console" />

      <main className={`fade-in ${showMainContent ? 'visible' : 'hidden'}`}>
        <h1 className="progress-title">
          [Q] Onboarding Progress: {NEXUS_PROJECT_NAME}
        </h1>

        <div className="progress-bar-container">
          <div
            className="progress-bar"
            style={{ width: `${completionPercentage}%` }}
          ></div>
        </div>

        <div className="actions-top">
          <select
            onChange={handleThemeChange}
            value={selectedTheme}
            className="theme-selector"
          >
            <option value="default">Theme</option>
            <option value="light-theme">Light</option>
            <option value="dark-theme">Dark</option>
            <option value="blue-theme">Blue</option>
            <option value="green-theme">Green</option>
            <option value="red-theme">Red</option>
            <option value="matrix-theme">Matrix</option>
          </select>

          {ranks.length > 0 && (
            <button
              className="score"
              style={getAverageRankStyles(averageRank)}
              disabled
            >
              Score: {averageRank} Progress: {completionPercentage}%
            </button>
          )}

          {currentQuestions[currentQuestionIndex]?.suggestion && (
            <button
              className="refresh"
              onClick={() =>
                handleRefresh(
                  currentQuestionIndex,
                  currentQuestions,
                  setCurrentQuestions,
                  setDisplayedText,
                  setAnswer,
                  setModalMessage,
                  setIsLoading,
                  submitButtonRef
                )
              }
              disabled={isLoading}
            >
              autofill
            </button>
          )}

          <button
            className="clear"
            onClick={handleClearContent}
            disabled={isLoading}
          >
            [×] Start Over
          </button>
        </div>

        {isGenerating ? (
          <>
            <div className="chat-link-container">
              {testPassed ? (
                <button
                  className="chat-link-button"
                  onClick={() => {
                
                    const w = window.open(
                      linkTarget,
                      qid, // window name
                      'noopener,noreferrer'
                    );

                    if (w) w.focus();
                  }}
                >
                  build
                </button>
              ) : (
                <button
                  className="chat-link-button"
                  onClick={() =>
                    window.open(
                      'https://nexus-platforms.com',
                      '_blank',
                      'noopener,noreferrer'
                    )
                  }
                >
                  [Q] Chrome Extension Required To Build
                </button>
              )}
            </div>
          </>
        ) : isLastQuestion(currentQuestions) && !hideForm ? (
          <Form
            handleGenerate={handleGenerate(
              currentQuestions,
              setIsGenerating,
              setModalMessage,
              setProgress,
              setTimeRemaining
            )}
          />
        ) : (
          <div
            className={`chat-window ${
              currentQuestions[currentQuestionIndex]?.suggestion
                ? 'has-suggestion'
                : ''
            } ${
              currentQuestions[currentQuestionIndex]?.description
                ? 'has-description'
                : ''
            }`}
            dangerouslySetInnerHTML={{ __html: displayedText }}
          />
        )}

        {showConsole && !isGenerating && (
          <div className="console-wrapper">
            <button
              className="console-close-button fade-in"
              onClick={closeConsole}
            >
              [×]
            </button>

            <div className="console-section formatted-items-section">
              <h2 className="console-heading">all answers</h2>

              <button
                className="console-copy-button"
                onClick={() =>
                  navigator.clipboard.writeText(
                    JSON.stringify(allAnswersForConsole, null, 2)
                  )
                }
              >
                Copy
              </button>

              <pre className="console-pre">
                {JSON.stringify(allAnswersForConsole, null, 2)}
              </pre>
            </div>

            <div className="console-section formatted-items-section">
              <h2 className="console-heading">export payload (id/a)</h2>

              <button
                className="console-copy-button"
                onClick={() =>
                  navigator.clipboard.writeText(
                    JSON.stringify(answeredOnlyForExport, null, 2)
                  )
                }
              >
                Copy
              </button>

              <pre className="console-pre">
                {JSON.stringify(answeredOnlyForExport, null, 2)}
              </pre>
            </div>

            <div className="console-section formatted-items-section">
              <h2 className="console-heading">legacy formatted output</h2>

              <button
                className="console-copy-button"
                onClick={() =>
                  navigator.clipboard.writeText(
                    JSON.stringify(formattedItems, null, 2)
                  )
                }
              >
                Copy
              </button>

              <pre className="console-pre">
                {JSON.stringify(formattedItems, null, 2)}
              </pre>
            </div>

            <div className="console-section formatted-questions-section">
              <h2 className="console-heading">[Q] questions (with formatting)</h2>

              <button
                className="console-copy-button"
                onClick={() =>
                  navigator.clipboard.writeText(
                    JSON.stringify(formattedQuestions, null, 2)
                  )
                }
              >
                Copy
              </button>

              <pre className="console-pre">
                {JSON.stringify(formattedQuestions, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {!isGenerating && !showConsole && (
          <>
            <button
              className={
                isFullsize
                  ? 'fullsize-toggle action-minimize'
                  : 'fullsize-toggle action-fullsize'
              }
              onClick={toggleFullsize}
            >
              {isFullsize ? 'minimize' : 'fullsize'}
            </button>

            <textarea
              ref={textareaRef}
              value={answer}
              onChange={handleInputChange}
              placeholder="enter your answer"
              className={`fade-in ${isFullsize ? 'fullsize' : ''}`}
              disabled={isLoading}
            ></textarea>

            <div className="actions-bottom">
              <button
                className="submit"
                onClick={handleSubmit}
                ref={submitButtonRef}
                disabled={isLoading}
              >
                Enter
              </button>

              <button className="console-button" onClick={toggleConsole}>
                Console
              </button>
            </div>

            <article>
              <section></section>
            </article>
          </>
        )}
      </main>

      <div className="footer nexus-footer">© nexus platforms 2026</div>

      <div
        className={`logo logo-animated ${
          showMainContent ? 'hidden' : 'visible'
        }`}
        onClick={() => setShowMainContent(true)}
      >
        <h2>[Q]</h2>
        <h2>[Q]</h2>
        <h2 className="home-title-subtext">got an idea? run it by [Q]!</h2>
        <h2 className="home-title-subtext">got an idea? run it by [Q]!</h2>
      </div>
    </div>
  );
}

export default App;